import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Copy,
  GripVertical,
  Lock,
  Pencil,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react";
import { Modal } from "../Modal";
import { ConfirmDialog, type ConfirmRequest } from "../ConfirmDialog";
import {
  ANSWERED_BY_LABELS,
  CHOICE_QUESTION_TYPES,
  QUESTION_TYPE_LABELS,
  SCORED_QUESTION_TYPES,
  appraisalService,
  type AnsweredBy,
  type AppraisalQuestion,
  type AppraisalSection,
  type QuestionType,
  type TemplateVersion,
} from "../../services/appraisalService";
import { apiErrorMessage } from "../../utils/apiError";

/**
 * The form builder for one template version.
 *
 * Everything here writes to a draft. A published version is the historical
 * record of a form people have already answered, so it is shown read-only and
 * the server refuses the writes anyway -- the UI only ever agrees with it,
 * never decides it.
 */

interface QuestionDraft {
  text: string;
  help_text: string;
  question_type: QuestionType;
  answered_by: AnsweredBy;
  is_required: boolean;
  weight: string;
  order: number;
  rating_scale_min: string;
  rating_scale_max: string;
  options: string;
  data_source: string;
  requires_evidence: boolean;
}

const blankQuestion = (order: number, answeredBy: AnsweredBy): QuestionDraft => ({
  text: "",
  help_text: "",
  question_type: "rating",
  answered_by: answeredBy,
  is_required: true,
  weight: "1",
  order,
  rating_scale_min: "",
  rating_scale_max: "",
  options: "",
  data_source: "",
  requires_evidence: false,
});

const questionToDraft = (question: AppraisalQuestion): QuestionDraft => ({
  text: question.text,
  help_text: question.help_text ?? "",
  question_type: question.question_type,
  answered_by: question.answered_by,
  is_required: question.is_required,
  weight: question.weight,
  order: question.order,
  rating_scale_min: question.rating_scale_min?.toString() ?? "",
  rating_scale_max: question.rating_scale_max?.toString() ?? "",
  options: (question.options ?? []).join("\n"),
  data_source: question.data_source ?? "",
  requires_evidence: question.requires_evidence,
});

/** Turn the form's strings into the API's shape, leaving out what is blank so
 * an untouched optional field is never sent as an empty string. */
function draftToPayload(draft: QuestionDraft, section: number) {
  const isChoice = CHOICE_QUESTION_TYPES.includes(draft.question_type);
  return {
    section,
    text: draft.text.trim(),
    help_text: draft.help_text.trim(),
    question_type: draft.question_type,
    answered_by: draft.answered_by,
    is_required: draft.is_required,
    weight: draft.weight || "1",
    order: draft.order,
    // A blank override means "use the version's scale", which the server
    // stores as null rather than as a number of its own.
    rating_scale_min:
      draft.question_type === "rating" && draft.rating_scale_min
        ? Number(draft.rating_scale_min)
        : null,
    rating_scale_max:
      draft.question_type === "rating" && draft.rating_scale_max
        ? Number(draft.rating_scale_max)
        : null,
    options: isChoice
      ? draft.options
          .split("\n")
          .map((o) => o.trim())
          .filter(Boolean)
      : [],
    data_source: draft.data_source,
    requires_evidence: draft.requires_evidence,
  };
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-secondary-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-secondary-500">{hint}</span>}
    </label>
  );
}

function QuestionDialog({
  open,
  title,
  draft,
  setDraft,
  onSubmit,
  onClose,
  saving,
  error,
}: {
  open: boolean;
  title: string;
  draft: QuestionDraft;
  setDraft: (next: QuestionDraft) => void;
  onSubmit: () => void;
  onClose: () => void;
  saving: boolean;
  error: string | null;
}) {
  const dataSources = useQuery({
    queryKey: ["appraisal-data-sources"],
    queryFn: appraisalService.getDataSources,
    enabled: open,
  });
  const isChoice = CHOICE_QUESTION_TYPES.includes(draft.question_type);
  const isScored = SCORED_QUESTION_TYPES.includes(draft.question_type);

  return (
    <Modal isOpen={open} title={title} onClose={onClose} size="2xl">
      <div className="space-y-4">
        <Field label="Question">
          <textarea
            className="input w-full resize-y py-2"
            rows={2}
            value={draft.text}
            placeholder="e.g. Punctuality and attendance"
            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
          />
        </Field>

        <Field
          label="Guidance"
          hint="Shown under the question. Use it to say how the answer should be judged."
        >
          <textarea
            className="input w-full resize-y py-2"
            rows={2}
            value={draft.help_text}
            onChange={(e) => setDraft({ ...draft, help_text: e.target.value })}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Answer type">
            <select
              className="input w-full"
              value={draft.question_type}
              onChange={(e) =>
                setDraft({ ...draft, question_type: e.target.value as QuestionType })
              }
            >
              {Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Answered by">
            <select
              className="input w-full"
              value={draft.answered_by}
              onChange={(e) =>
                setDraft({ ...draft, answered_by: e.target.value as AnsweredBy })
              }
            >
              {Object.entries(ANSWERED_BY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {isChoice && (
          <Field label="Options" hint="One per line. A choice question needs at least one.">
            <textarea
              className="input w-full resize-y py-2 font-mono text-xs"
              rows={4}
              value={draft.options}
              placeholder={"Always\nUsually\nSometimes\nRarely"}
              onChange={(e) => setDraft({ ...draft, options: e.target.value })}
            />
          </Field>
        )}

        {draft.question_type === "rating" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Scale from" hint="Leave blank to use the version's scale.">
              <input
                type="number"
                className="input w-full"
                value={draft.rating_scale_min}
                onChange={(e) =>
                  setDraft({ ...draft, rating_scale_min: e.target.value })
                }
              />
            </Field>
            <Field label="Scale to">
              <input
                type="number"
                className="input w-full"
                value={draft.rating_scale_max}
                onChange={(e) =>
                  setDraft({ ...draft, rating_scale_max: e.target.value })
                }
              />
            </Field>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Evidence to show beside it"
            hint="School records shown as context. Showing them never scores the question."
          >
            <select
              className="input w-full"
              value={draft.data_source}
              onChange={(e) => setDraft({ ...draft, data_source: e.target.value })}
            >
              <option value="">None</option>
              {(dataSources.data ?? []).map((source) => (
                <option key={source.slug} value={source.slug}>
                  {source.label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Weight"
            hint={
              isScored
                ? "How much this counts towards the section score."
                : "Only scored questions (rating, number, percentage) count towards a result."
            }
          >
            <input
              type="number"
              step="0.1"
              min="0"
              className="input w-full"
              value={draft.weight}
              onChange={(e) => setDraft({ ...draft, weight: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Position">
            <input
              type="number"
              className="input w-full"
              value={draft.order}
              onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })}
            />
          </Field>
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-secondary-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-secondary-300"
              checked={draft.is_required}
              onChange={(e) => setDraft({ ...draft, is_required: e.target.checked })}
            />
            Required
          </label>
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-secondary-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-secondary-300"
              checked={draft.requires_evidence}
              onChange={(e) =>
                setDraft({ ...draft, requires_evidence: e.target.checked })
              }
            />
            Needs an attachment
          </label>
        </div>

        {error && (
          <p className="rounded-lg border border-maroon-200 bg-maroon-50 px-3 py-2 text-sm text-maroon-800">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 border-t border-secondary-100 pt-4">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn bg-navy-700 text-white hover:bg-navy-800"
            disabled={!draft.text.trim() || saving}
            onClick={onSubmit}
          >
            {saving ? "Saving…" : "Save question"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SectionDialog({
  open,
  title,
  draft,
  setDraft,
  onSubmit,
  onClose,
  saving,
  error,
}: {
  open: boolean;
  title: string;
  draft: { title: string; description: string; order: number; weight: string; answered_by: AnsweredBy };
  setDraft: (next: typeof draft) => void;
  onSubmit: () => void;
  onClose: () => void;
  saving: boolean;
  error: string | null;
}) {
  return (
    <Modal isOpen={open} title={title} onClose={onClose} size="lg">
      <div className="space-y-4">
        <Field label="Section title">
          <input
            className="input w-full"
            value={draft.title}
            placeholder="e.g. Professional conduct"
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
        </Field>
        <Field label="Description" hint="Optional. Shown under the section heading.">
          <textarea
            className="input w-full resize-y py-2"
            rows={2}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Position">
            <input
              type="number"
              className="input w-full"
              value={draft.order}
              onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })}
            />
          </Field>
          <Field label="Weight">
            <input
              type="number"
              step="0.1"
              min="0"
              className="input w-full"
              value={draft.weight}
              onChange={(e) => setDraft({ ...draft, weight: e.target.value })}
            />
          </Field>
          <Field label="Answered by">
            <select
              className="input w-full"
              value={draft.answered_by}
              onChange={(e) =>
                setDraft({ ...draft, answered_by: e.target.value as AnsweredBy })
              }
            >
              {Object.entries(ANSWERED_BY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {error && (
          <p className="rounded-lg border border-maroon-200 bg-maroon-50 px-3 py-2 text-sm text-maroon-800">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 border-t border-secondary-100 pt-4">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn bg-navy-700 text-white hover:bg-navy-800"
            disabled={!draft.title.trim() || saving}
            onClick={onSubmit}
          >
            {saving ? "Saving…" : "Save section"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function TemplateVersionEditor({
  version,
  canEdit,
  onRevise,
  revising = false,
}: {
  version: TemplateVersion;
  canEdit: boolean;
  /** Start a draft copy of this version. Offered from inside the frozen
   * notice, because "create a new version" is the answer to "why can I not
   * edit this" and the two belong in the same place. */
  onRevise?: () => void;
  revising?: boolean;
}) {
  const queryClient = useQueryClient();
  const editable = canEdit && !version.is_published;

  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const [sectionDialog, setSectionDialog] = useState<{
    id: number | null;
    draft: { title: string; description: string; order: number; weight: string; answered_by: AnsweredBy };
  } | null>(null);
  const [questionDialog, setQuestionDialog] = useState<{
    id: number | null;
    section: number;
    draft: QuestionDraft;
  } | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["appraisal-templates"] });
  };

  const saveSection = useMutation({
    mutationFn: async () => {
      if (!sectionDialog) return;
      const body = { ...sectionDialog.draft, template_version: version.id };
      return sectionDialog.id === null
        ? appraisalService.createSection(body)
        : appraisalService.updateSection(sectionDialog.id, body);
    },
    onSuccess: () => {
      setSectionDialog(null);
      setError(null);
      refresh();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "That section could not be saved.")),
  });

  const saveQuestion = useMutation({
    mutationFn: async () => {
      if (!questionDialog) return;
      const body = draftToPayload(questionDialog.draft, questionDialog.section);
      return questionDialog.id === null
        ? appraisalService.createQuestion(body)
        : appraisalService.updateQuestion(questionDialog.id, body);
    },
    onSuccess: () => {
      setQuestionDialog(null);
      setError(null);
      refresh();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "That question could not be saved.")),
  });

  const removeSection = useMutation({
    mutationFn: (id: number) => appraisalService.deleteSection(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "That section could not be removed.")),
  });

  const removeQuestion = useMutation({
    mutationFn: (id: number) => appraisalService.deleteQuestion(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "That question could not be removed.")),
  });

  const saveSettings = useMutation({
    mutationFn: (body: Partial<TemplateVersion>) =>
      appraisalService.updateVersion(version.id, body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Those settings could not be saved.")),
  });

  const sections = [...(version.sections ?? [])].sort((a, b) => a.order - b.order);
  const nextSectionOrder = sections.length
    ? Math.max(...sections.map((s) => s.order)) + 1
    : 1;

  return (
    <div className="space-y-3">
      {!editable && (
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2.5">
          <p className="flex items-start gap-2 text-xs text-secondary-600">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {version.is_published
              ? "Published, so it is frozen: this is the form people were actually asked. The questions below are read-only. To change them, revise it as a new version — this one, and every appraisal answered against it, stays as it is."
              : "You do not have permission to edit appraisal templates. Ask an administrator for the “Appraisal: manage templates” capability."}
          </p>
          {version.is_published && canEdit && onRevise && (
            <button
              type="button"
              className="btn btn-secondary btn-sm shrink-0"
              disabled={revising}
              onClick={onRevise}
            >
              <Copy className="mr-1 h-3.5 w-3.5" aria-hidden />
              {revising ? "Creating…" : "Revise in a new version"}
            </button>
          )}
        </div>
      )}

      {error && (
        <p
          className="flex items-start gap-2 rounded-lg border border-maroon-200 bg-maroon-50 px-3 py-2 text-sm text-maroon-800"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {error}
        </p>
      )}

      {/* Version-wide settings */}
      {editable && (
        <div className="rounded-lg border border-secondary-200">
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-secondary-700 hover:bg-secondary-50"
            onClick={() => setShowSettings((open) => !open)}
          >
            {showSettings ? (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            )}
            <Settings2 className="h-3.5 w-3.5 text-secondary-400" aria-hidden />
            Stages and rating scale
          </button>
          {showSettings && (
            <div className="space-y-3 border-t border-secondary-100 p-3">
              <div className="flex flex-wrap gap-4">
                {(
                  [
                    ["has_self_assessment", "Self-assessment"],
                    ["has_review_meeting", "Review meeting"],
                    ["has_development_planning", "Development planning"],
                  ] as const
                ).map(([key, label]) => (
                  <label
                    key={key}
                    className="flex items-center gap-2 text-sm text-secondary-700"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-secondary-300"
                      checked={version[key]}
                      disabled={saveSettings.isPending}
                      onChange={(e) =>
                        saveSettings.mutate({ [key]: e.target.checked })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Rating scale from">
                  <input
                    type="number"
                    className="input w-24"
                    defaultValue={version.rating_scale_min}
                    onBlur={(e) =>
                      Number(e.target.value) !== version.rating_scale_min &&
                      saveSettings.mutate({ rating_scale_min: Number(e.target.value) })
                    }
                  />
                </Field>
                <Field label="to">
                  <input
                    type="number"
                    className="input w-24"
                    defaultValue={version.rating_scale_max}
                    onBlur={(e) =>
                      Number(e.target.value) !== version.rating_scale_max &&
                      saveSettings.mutate({ rating_scale_max: Number(e.target.value) })
                    }
                  />
                </Field>
                <p className="pb-2 text-xs text-secondary-500">
                  The default for every rating question. A question can override it.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sections */}
      {sections.length === 0 && (
        <p className="rounded-lg border border-dashed border-secondary-300 px-3 py-6 text-center text-sm text-secondary-500">
          No sections yet. A form needs at least one section with one question
          before it can be published.
        </p>
      )}

      {sections.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          editable={editable}
          onEditSection={() =>
            setSectionDialog({
              id: section.id,
              draft: {
                title: section.title,
                description: section.description ?? "",
                order: section.order,
                weight: section.weight,
                answered_by: section.answered_by,
              },
            })
          }
          onDeleteSection={() =>
            setConfirm({
              title: "Remove this section?",
              body: `“${section.title}” and its ${section.questions.length} question${
                section.questions.length === 1 ? "" : "s"
              } will be removed from this draft.`,
              confirmLabel: "Remove",
              tone: "danger",
              onConfirm: () => removeSection.mutate(section.id),
            })
          }
          onAddQuestion={() =>
            setQuestionDialog({
              id: null,
              section: section.id,
              draft: blankQuestion(
                section.questions.length
                  ? Math.max(...section.questions.map((q) => q.order)) + 1
                  : 1,
                section.answered_by,
              ),
            })
          }
          onEditQuestion={(question) =>
            setQuestionDialog({
              id: question.id,
              section: section.id,
              draft: questionToDraft(question),
            })
          }
          onDeleteQuestion={(question) =>
            setConfirm({
              title: "Remove this question?",
              body: `“${question.text}” will be removed from this draft.`,
              confirmLabel: "Remove",
              tone: "danger",
              onConfirm: () => removeQuestion.mutate(question.id),
            })
          }
        />
      ))}

      {editable && (
        <button
          type="button"
          className="btn btn-outline w-full border-dashed"
          onClick={() =>
            setSectionDialog({
              id: null,
              draft: {
                title: "",
                description: "",
                order: nextSectionOrder,
                weight: "1",
                answered_by: "both",
              },
            })
          }
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          Add section
        </button>
      )}

      {sectionDialog && (
        <SectionDialog
          open
          title={sectionDialog.id === null ? "New section" : "Edit section"}
          draft={sectionDialog.draft}
          setDraft={(draft) => setSectionDialog({ ...sectionDialog, draft })}
          onSubmit={() => saveSection.mutate()}
          onClose={() => {
            setSectionDialog(null);
            setError(null);
          }}
          saving={saveSection.isPending}
          error={error}
        />
      )}

      {questionDialog && (
        <QuestionDialog
          open
          title={questionDialog.id === null ? "New question" : "Edit question"}
          draft={questionDialog.draft}
          setDraft={(draft) => setQuestionDialog({ ...questionDialog, draft })}
          onSubmit={() => saveQuestion.mutate()}
          onClose={() => {
            setQuestionDialog(null);
            setError(null);
          }}
          saving={saveQuestion.isPending}
          error={error}
        />
      )}

      <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function SectionBlock({
  section,
  editable,
  onEditSection,
  onDeleteSection,
  onAddQuestion,
  onEditQuestion,
  onDeleteQuestion,
}: {
  section: AppraisalSection;
  editable: boolean;
  onEditSection: () => void;
  onDeleteSection: () => void;
  onAddQuestion: () => void;
  onEditQuestion: (question: AppraisalQuestion) => void;
  onDeleteQuestion: (question: AppraisalQuestion) => void;
}) {
  const questions = [...section.questions].sort((a, b) => a.order - b.order);

  return (
    <div className="overflow-hidden rounded-lg border border-secondary-200">
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-secondary-100 bg-secondary-50/70 px-3 py-2">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-secondary-900">{section.title}</h4>
          <p className="text-xs text-secondary-500">
            {ANSWERED_BY_LABELS[section.answered_by]} · weight {section.weight} ·{" "}
            {questions.length} question{questions.length === 1 ? "" : "s"}
          </p>
        </div>
        {editable && (
          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              className="rounded p-1.5 text-secondary-500 hover:bg-white hover:text-navy-700"
              onClick={onEditSection}
              aria-label={`Edit section ${section.title}`}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              className="rounded p-1.5 text-secondary-500 hover:bg-white hover:text-maroon-700"
              onClick={onDeleteSection}
              aria-label={`Remove section ${section.title}`}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        )}
      </div>

      <ul className="divide-y divide-secondary-100">
        {questions.length === 0 && (
          <li className="px-3 py-3 text-xs italic text-secondary-400">
            No questions in this section yet.
          </li>
        )}
        {questions.map((question) => (
          <li key={question.id} className="flex items-start gap-2 px-3 py-2.5">
            <GripVertical
              className="mt-0.5 h-4 w-4 shrink-0 text-secondary-300"
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-secondary-900">
                {question.text}
                {question.is_required && (
                  <span className="relative ml-1 text-maroon-600" title="Required">
                    *<span className="sr-only">required</span>
                  </span>
                )}
              </p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-secondary-500">
                <span className="rounded bg-secondary-100 px-1.5 py-0.5 font-medium">
                  {QUESTION_TYPE_LABELS[question.question_type]}
                </span>
                <span>{ANSWERED_BY_LABELS[question.answered_by]}</span>
                {question.question_type === "rating" && (
                  <span>
                    scale {question.effective_scale.min}–{question.effective_scale.max}
                  </span>
                )}
                {question.data_source && (
                  <span className="rounded bg-navy-50 px-1.5 py-0.5 capitalize text-navy-700">
                    {question.data_source.replace(/_/g, " ")}
                  </span>
                )}
                {question.scoring_rule && (
                  <span className="rounded bg-gold-100 px-1.5 py-0.5 text-gold-700">
                    scored from evidence
                  </span>
                )}
              </p>
            </div>
            {editable && (
              <div className="flex shrink-0 gap-1">
                <button
                  type="button"
                  className="rounded p-1.5 text-secondary-500 hover:bg-secondary-50 hover:text-navy-700"
                  onClick={() => onEditQuestion(question)}
                  aria-label={`Edit question ${question.text}`}
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden />
                </button>
                <button
                  type="button"
                  className="rounded p-1.5 text-secondary-500 hover:bg-secondary-50 hover:text-maroon-700"
                  onClick={() => onDeleteQuestion(question)}
                  aria-label={`Remove question ${question.text}`}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {editable && (
        <div className="border-t border-secondary-100 px-3 py-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-semibold text-navy-700 hover:text-navy-900"
            onClick={onAddQuestion}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add question
          </button>
        </div>
      )}
    </div>
  );
}
